import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import ProductAutocomplete from '../components/ProductAutocomplete';
import {
  useFindProductValidations,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  BaseProductAssignDialogProps,
  ProductValidation
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

export interface AssignValidationDialogProps
  extends Omit<
    BaseProductAssignDialogProps<never, ProductValidation>,
    'onAssigned' | 'productId'
  > {
  productId?: number;
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (validations: ProductValidation[]) => void;
}

const AssignValidationDialog: React.FC<AssignValidationDialogProps> = props => {
  const {
    open,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = `Assign ${getProductEntityName('validation')}`,
    contentText = `Search for ${getProductEntityName('validation')} to assign.`,
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [productId, setProductId] = useState(props.productId);
  const {
    productValidationList,
    isLoadingProductValidations,
    loadingProductValidationsError,
    findProductValidations
  } = useFindProductValidations(productId || 0);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.Validation
  );

  // handle searching
  useEffect(() => {
    const params: ApiParams = { search, page, limit };
    findProductValidations(params);
  }, [search, limit, page, productId, findProductValidations]);

  useEffect(() => {
    if (loadingProductValidationsError) {
      onError?.(loadingProductValidationsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductValidationsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label={getProductEntityName('validation')}
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <Box mt={1} mb={2} className='search-box'>
          <ProductAutocomplete
            productId={productId}
            onChange={product => {
              if (product) {
                setProductId(product.id);
              }
            }}
          />
        </Box>
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: ProductValidation) => prefix + item.id
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: ProductValidation) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductValidations || isLoading}
          dataList={productValidationList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductValidationsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignValidationDialog;
